import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { number, string } from 'prop-types';

import ButtonClean from '~/components/ButtonClean';
import { CookieConsentsContext } from '~/components/CookieConsentsProvider';
import Markdown from '~/components/Markdown';

import { ECookieProcessingServices } from '~/constants/cmp';
import { youtubeCookieConsentSelector } from '~/redux/selectors';
import { setYoutubeCookieConsent } from '~/redux/slices/misc';

import { ReactComponent as ArrowCircleIcon } from '~/assets/images/icons/i-arrow-right-circle.svg';
import { ReactComponent as PlayIcon } from '~/assets/images/icons/i-play.svg';

import styles from './YoutubePlayer.module.scss';

const getThumbnailSourceSet = (videoId) => ({
  small: `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
  large: `https://i.ytimg.com/vi/${videoId}/0.jpg`,
  largest: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
});

const YouTubePlayer = ({ videoId, startAt, autoplay }) => {
  const dispatch = useDispatch();
  const youtubeCookieConsent = useSelector(youtubeCookieConsentSelector);
  const {
    consents: { [ECookieProcessingServices.YOUTUBE]: hasYouTubeConsent },
    acceptService,
  } = useContext(CookieConsentsContext);
  const thumbnails = getThumbnailSourceSet(videoId);
  const iframeSrc = `https://www.youtube.com/embed/${videoId}?start=${
    startAt || 0
  }&origin=${window.location.origin}&autoplay=${autoplay || 0}`;

  const onLoadVideoClick = () => {
    dispatch(setYoutubeCookieConsent(true));
    acceptService(ECookieProcessingServices.YOUTUBE).catch(() => {
      dispatch(setYoutubeCookieConsent(false));
    });
  };

  return (
    <div className="youTube">
      {youtubeCookieConsent && hasYouTubeConsent ? (
        <iframe
          title="YouTube Video"
          key={videoId}
          id={videoId}
          frameBorder={0}
          uc-src={iframeSrc}
        />
      ) : (
        <div className={styles.overlay}>
          <div className={styles.consentInfo}>
            <ArrowCircleIcon />
            <Markdown>{I18n.t('common_youtube_cookie_overlay')}</Markdown>
          </div>
          <ButtonClean
            className={styles.consentButton}
            type="button"
            onClick={onLoadVideoClick}
          >
            <PlayIcon />
            <p>{I18n.t('common_youtube_cookie_overlay_pb')}</p>
          </ButtonClean>
        </div>
      )}
      <img
        src={thumbnails.small}
        srcSet={`${thumbnails.large} 2x, ${thumbnails.largest} 3x`}
        alt="YouTube video thumbnail"
        className={styles.thumbnail}
      />
    </div>
  );
};

YouTubePlayer.propTypes = {
  videoId: string.isRequired,
  startAt: number,
  autoplay: number,
};

export default YouTubePlayer;
